/**
 * Write-side offerings hooks: create, update (optimistic + rollback), delete.
 *
 * Cache invalidation keys MUST stay identical to the prior inline
 * implementation — composed back into `useOfferingHooks`.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Offering } from "@/lib/types/database/offerings";
import { OFFERINGS_QUERY_KEY, offeringsKeys } from "./offerings-query-keys";
import { deleteOffering, insertOffering, updateOffering } from "./offerings-fetchers";

interface MutationReturn<TInput, TOutput> {
  mutateAsync: (input: TInput) => Promise<TOutput>;
  isPending: boolean;
}

type UpdateOfferingContext = {
  previousOfferings: Offering[] | undefined;
  previousOffering: Offering | undefined;
  previousPaginated: { data: Offering[] } | undefined;
};

export function useCreateOffering(): MutationReturn<Partial<Offering> & { id?: string }, Offering> {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: insertOffering,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OFFERINGS_QUERY_KEY });
      toast.success("Offering created successfully");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useUpdateOffering(): MutationReturn<Partial<Offering> & { id: string }, Offering> {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: updateOffering,
    onMutate: async (input): Promise<UpdateOfferingContext> => {
      const { id } = input;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: OFFERINGS_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: offeringsKeys.single(id) });
      await queryClient.cancelQueries({ queryKey: offeringsKeys.paginatedBase() });

      // Snapshot previous values
      const previousOfferings = queryClient.getQueryData<Offering[]>(OFFERINGS_QUERY_KEY);
      const previousOffering = queryClient.getQueryData<Offering>(offeringsKeys.single(id));
      const previousPaginated = queryClient.getQueryData<{ data: Offering[] }>(
        offeringsKeys.paginatedBase()
      );

      // Optimistically update single offering cache
      queryClient.setQueryData(offeringsKeys.single(id), (old: Offering | undefined) => {
        if (!old) return old;
        const { id: _id, ...updates } = input;
        return { ...old, ...updates };
      });

      // Optimistically update offerings list
      queryClient.setQueryData(OFFERINGS_QUERY_KEY, (old: Offering[] | undefined) => {
        if (!old) return old;
        const { id: _id, ...updates } = input;
        return old.map((offering) => (offering.id === id ? { ...offering, ...updates } : offering));
      });

      // Optimistically update paginated offerings
      queryClient.setQueryData(
        offeringsKeys.paginatedBase(),
        (old: { data: Offering[] } | undefined) => {
          if (!old) return old;
          const { id: _id, ...updates } = input;
          return {
            ...old,
            data: old.data?.map((offering: Offering) =>
              offering.id === id ? { ...offering, ...updates } : offering
            ),
          };
        }
      );

      return { previousOfferings, previousOffering, previousPaginated };
    },
    onSuccess: (data) => {
      // Update caches with server response
      queryClient.setQueryData(offeringsKeys.single(data.id), data);

      queryClient.setQueryData(OFFERINGS_QUERY_KEY, (old: Offering[] | undefined) => {
        if (!old) return old;
        return old.map((offering) => (offering.id === data.id ? data : offering));
      });

      queryClient.setQueryData(
        offeringsKeys.paginatedBase(),
        (old: { data: Offering[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data?.map((offering: Offering) =>
              offering.id === data.id ? data : offering
            ),
          };
        }
      );

      toast.success("Offering updated successfully");
    },
    onError: (_error, _variables, onMutateResult: UpdateOfferingContext | undefined) => {
      // Rollback to previous values
      if (onMutateResult?.previousOfferings) {
        queryClient.setQueryData(OFFERINGS_QUERY_KEY, onMutateResult.previousOfferings);
      }
      if (onMutateResult?.previousOffering) {
        queryClient.setQueryData(
          offeringsKeys.single(_variables.id),
          onMutateResult.previousOffering
        );
      }
      if (onMutateResult?.previousPaginated) {
        queryClient.setQueryData(offeringsKeys.paginatedBase(), onMutateResult.previousPaginated);
      }

      toast.error("Failed to update offering");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}

export function useDeleteOffering(): MutationReturn<string, string> {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: deleteOffering,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OFFERINGS_QUERY_KEY });
      toast.success("Offering deleted successfully");
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
